import { useCallback, useState } from 'react';
import { MailStub, Res } from 'api/api.types';
import fileDownload from 'js-file-download';
import {
  AllocationStatus,
  InvoiceStatus,
  PaymentStatus,
} from 'pages/invoices/components/filters/filters.types';
import {
  Clause,
  CorrectiveInvoice,
  CurrencyCode,
  Invoice,
  InvoiceClass,
} from 'pages/invoices/invoices.types';
import { InvoiceType } from 'root/pathnames';
import { InvoicesParams } from 'types/invoices';
import { Activity } from '../activities/activities.types';
import { axiosInstance, useAxios } from '../axios';
import { repeatArraySerializer } from '../utils';
import {
  CheckUnpublishedResult,
  ClausesQueryParams,
  CreateCorrectiveInvoiceBody,
  CreateInvoiceBody,
  CreateJobInvoiceBody,
  CurrencyQueryParams,
  EditCorrectiveInvoiceBody,
  EditInvoiceBody,
  ExchangeRateDateParams,
  ExchangeRateExistsParams,
  ExchangeRateValue,
  ExchangeRateValueParams,
  EnovaFetchResult,
  FetchResult,
  IExchangeRateExists,
  InvoiceClassQueryParams,
  InvoicePublicationResult,
  LastIssueDateParams,
  MailReqBody,
  ExportResult,
} from './invoices.types';

export const useGetInvoices = () => {
  const [{ data, loading }, getInvoicesReq] = useAxios<{
    result: { data: Invoice[]; count: number };
  }>({
    method: 'GET',
  });

  const getInvoices = useCallback(
    ({
      pageNumber,
      resultSize,
      own,
      administrative,
      status,
      invoiceStatus,
      np,
      onlyMine,
      startDate,
      endDate,
      filterText,
      allocationStatus,
      excludeCorrective,
      excludeDeleted,
      published,
      invoiceType,
      forCorrectiveCreate,
      hasCorrectiveInvoice,
      sortProperty,
      sortDirection,
    }: InvoicesParams) =>
      getInvoicesReq({
        url: '/invoice',
        params: {
          ...(pageNumber && { pageNumber }),
          ...(resultSize && { resultSize }),
          ...(own ? { own: 'true' } : { own: 'false' }),
          ...(administrative && { administrative }),
          ...(filterText && { filterText }),
          np,
          onlyMine,
          ...(startDate && { startDate: startDate.valueOf() }),
          ...(endDate && { endDate: endDate.valueOf() }),
          ...(status &&
            (own || administrative) && {
              status: status !== PaymentStatus.ALL ? status : undefined,
            }),
          ...(invoiceStatus &&
            (own || administrative) && {
              invoiceStatus:
                invoiceStatus !== InvoiceStatus.ALL ? invoiceStatus : undefined,
            }),
          ...(allocationStatus !== AllocationStatus.ALL &&
            !own &&
            !administrative && {
              allocationStatus,
            }),
          ...(excludeCorrective && { excludeCorrective }),
          ...(excludeDeleted && { excludeDeleted }),
          ...(invoiceType && { invoiceType }),
          published,
          forCorrectiveCreate,
          hasCorrectiveInvoice,
          sortProperty,
          sortDirection,
        },
      }),
    [getInvoicesReq],
  );
  const invoices = data?.result?.data;
  const count = data?.result?.count;

  return {
    invoicesData:
      invoices && typeof count === 'number' ? { invoices, count } : undefined,
    loading,
    getInvoices,
  };
};

export const useGetInvoiceClasses = () => {
  const [{ data, loading }, getInvoiceClassesReq] = useAxios<{
    result: { data: InvoiceClass[] };
  }>({
    method: 'GET',
  });

  const getInvoiceClasses = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 100,
      forNewInvoice = true,
      forNewJobInvoice = false,
    }: InvoiceClassQueryParams) =>
      getInvoiceClassesReq({
        url: '/invoiceclass',
        params: {
          ...(filterText && { filterText }),
          ...(pageNumber && { pageNumber }),
          ...(resultSize && { resultSize }),
          ...{
            forNewInvoice,
          },
          ...{ forNewJobInvoice },
        },
      }),
    [getInvoiceClassesReq],
  );

  return {
    invoiceClassesData: data?.result.data,
    loading,
    getInvoiceClasses,
  };
};

export const useGetCurrencies = () => {
  const [{ data, loading }, getCurrenciesReq] = useAxios<{
    result: { data: CurrencyCode[] };
  }>({
    method: 'GET',
  });

  const getCurrencies = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 100,
      onlyVisible = true,
    }: CurrencyQueryParams) =>
      getCurrenciesReq({
        url: '/currency',
        params: {
          ...(filterText && { filterText }),
          ...(pageNumber && { pageNumber }),
          ...(resultSize && { resultSize }),
          ...{
            onlyVisible,
          },
        },
      }),
    [getCurrenciesReq],
  );

  return {
    currenciesData: data?.result.data,
    loading,
    getCurrencies,
  };
};

export const useGetClauses = () => {
  const [{ data, loading }, getClausesReq] = useAxios<{
    result: { data: Clause[] };
  }>(
    {
      method: 'GET',
    },
    {
      autoCancel: false,
    },
  );

  const getClauses = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 100,
      initial,
      documentType = 'INVOICE',
      clauseType,
      mandatory,
    }: ClausesQueryParams) =>
      getClausesReq({
        url: '/clause',
        params: {
          ...(filterText && { filterText }),
          ...(pageNumber && { pageNumber }),
          ...(resultSize && { resultSize }),
          ...(documentType && { documentType }),
          ...(clauseType && { clauseType }),
          ...{ initial },
          ...{ mandatory },
        },
      }),
    [getClausesReq],
  );

  return {
    clausesData: data?.result.data,
    loading,
    getClauses,
  };
};

export const useGetInvoiceById = () => {
  const [{ data, loading }, getInvoiceByIdReq] = useAxios<{
    result: Invoice | CorrectiveInvoice;
  }>({
    method: 'GET',
  });

  const getInvoiceById = useCallback(
    (id: number, isCorrective = false) => {
      const endpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return getInvoiceByIdReq({ url: `/${endpoint}/${id}` });
    },
    [getInvoiceByIdReq],
  );

  return {
    data: data?.result,
    loading,
    getInvoiceById,
  };
};

export const useGetLastIssueDate = () => {
  const [{ data, loading }, getLastIssueDateReq] = useAxios<{
    result: number;
  }>({
    method: 'GET',
  });

  const getLastIssueDate = useCallback(
    (endpoint: InvoiceType, { issueDate, type }: LastIssueDateParams) =>
      getLastIssueDateReq({
        url: `/${endpoint}/getLastIssueDate`,
        params: {
          ...(issueDate && { issueDate }),
          ...(type && { type }),
        },
      }),
    [getLastIssueDateReq],
  );

  return {
    lastIssueDateData: data?.result,
    loading,
    getLastIssueDate,
  };
};

export const useGetExchangeRateDate = () => {
  const [{ data, loading }, getExchangeRateDateReq] = useAxios<{
    result: number;
  }>({
    method: 'GET',
  });

  const getExchangeRateDate = useCallback(
    ({ saleDate }: ExchangeRateDateParams) =>
      getExchangeRateDateReq({
        url: '/invoice/getExchangeRateDate',
        params: {
          ...(saleDate && { saleDate }),
        },
      }),
    [getExchangeRateDateReq],
  );

  return {
    exchangeRateDateData: data?.result,
    loading,
    getExchangeRateDate,
  };
};

export const useGetExchangeRateExists = () => {
  const [{ data, loading }, getExchangeRateExistsReq] = useAxios<{
    result: IExchangeRateExists;
  }>({
    method: 'GET',
  });

  const getExchangeRateExists = useCallback(
    ({ to, date }: ExchangeRateExistsParams) =>
      getExchangeRateExistsReq({
        url: '/exchangerate/exists',
        params: {
          ...(to && { to }),
          ...(date && { date }),
        },
      }),
    [getExchangeRateExistsReq],
  );

  return {
    exchangeRateExistsData: data?.result,
    loading,
    getExchangeRateExists,
  };
};

export const useGetExchangeRateValue = () => {
  const [{ data, loading }, getExchangeRateValueReq] = useAxios<{
    result: ExchangeRateValue[];
  }>({
    method: 'GET',
  });

  const getExchangeRateValue = useCallback(
    ({ to, from, date }: ExchangeRateValueParams) =>
      getExchangeRateValueReq({
        url: '/exchangerate/value',
        params: {
          from,
          to,
          date,
        },
        paramsSerializer: repeatArraySerializer,
      }),
    [getExchangeRateValueReq],
  );

  return {
    exchangeRateValueData: data?.result,
    loading,
    getExchangeRateValue,
  };
};

export const useCreateInvoice = () => {
  const [{ data, loading }, getCreateInvoiceReq] = useAxios<Res<Invoice>>({
    method: 'POST',
  });

  const createInvoice = useCallback(
    (newInvoice: CreateInvoiceBody) =>
      getCreateInvoiceReq({
        url: '/invoice',
        data: newInvoice,
      }),
    [getCreateInvoiceReq],
  );

  return {
    createdInvoiceData: data?.result,
    loading,
    createInvoice,
  };
};

export const useCreateCorrectiveInvoice = () => {
  const [{ data, loading }, getCreateInvoiceReq] = useAxios<
    Res<CorrectiveInvoice>
  >({
    method: 'POST',
  });

  const createCorrectiveInvoice = useCallback(
    (newInvoice: CreateCorrectiveInvoiceBody) =>
      getCreateInvoiceReq({
        url: '/correctiveinvoice',
        data: newInvoice,
      }),
    [getCreateInvoiceReq],
  );

  return {
    createdCorrectiveInvoiceData: data?.result,
    loading,
    createCorrectiveInvoice,
  };
};

export const useEditInvoice = () => {
  const [{ data, loading }, getEditInvoiceReq] = useAxios<Res<Invoice>>({
    method: 'PUT',
  });

  const editInvoice = useCallback(
    (id: number, editedInvoice: EditInvoiceBody) =>
      getEditInvoiceReq({
        url: `/invoice/${id}`,
        data: editedInvoice,
      }),
    [getEditInvoiceReq],
  );

  return {
    editedInvoiceData: data?.result,
    loading,
    editInvoice,
  };
};

export const useEditCorrectiveInvoice = () => {
  const [{ data, loading }, getEditCorrectiveInvoiceReq] = useAxios<
    Res<CorrectiveInvoice>
  >({
    method: 'PUT',
  });

  const editCorrectiveInvoice = useCallback(
    (id: number, editedCorrectiveInvoice: EditCorrectiveInvoiceBody) =>
      getEditCorrectiveInvoiceReq({
        url: `/correctiveinvoice/${id}`,
        data: editedCorrectiveInvoice,
      }),
    [getEditCorrectiveInvoiceReq],
  );

  return {
    editedCorrectiveInvoiceData: data?.result,
    loading,
    editCorrectiveInvoice,
  };
};

export const useGetInvoiceActivities = () => {
  const [{ data, loading }, getInvoiceActivitiesReq] = useAxios<{
    result: Activity[];
  }>({
    method: 'GET',
  });

  const getInvoiceActivities = useCallback(
    (id: number, isCorrective: boolean) => {
      const endpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return getInvoiceActivitiesReq({ url: `/${endpoint}/${id}/activities` });
    },
    [getInvoiceActivitiesReq],
  );

  return {
    data: data?.result,
    loading,
    getInvoiceActivities,
  };
};

export const useCancelInvoice = () => {
  const [{ loading }, cancelInvoiceReq] = useAxios<Res<null>>(
    {
      method: 'DELETE',
    },
    {
      autoCancel: false,
    },
  );

  const cancelInvoice = useCallback(
    (id: number, isCorrective: boolean) => {
      const endpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return cancelInvoiceReq({
        url: `/${endpoint}/${id}`,
      });
    },
    [cancelInvoiceReq],
  );

  return {
    loading,
    cancelInvoice,
  };
};

export const useExportInvoice = () => {
  const [isLoading, setIsLoading] = useState(false);

  const exportInvoice = useCallback((invoiceIDs: number[]) => {
    setIsLoading(true);
    return axiosInstance
      .get<Blob | Res<ExportResult>>(`/accountingdocument/export`, {
        params: { invoiceIDs },
        paramsSerializer: repeatArraySerializer,
        responseType: 'blob',
      })
      .then(async (res) => {
        const contentDisposition = res.headers['content-disposition'];

        if (
          res.data instanceof Blob &&
          contentDisposition?.includes('attachment')
        ) {
          const fileName = contentDisposition.split('filename=')[1] || 'export';
          fileDownload(res.data, fileName);
          return { type: 'file' as const };
        }

        const json: Res<ExportResult> =
          res.data instanceof Blob
            ? JSON.parse(await res.data.text())
            : res.data;
        return { type: 'json' as const, data: json };
      })
      .finally(() => setIsLoading(false));
  }, []);

  return {
    loading: isLoading,
    exportInvoice,
  };
};

export const useChangeInvoiceStatus = () => {
  const [{ loading }, changeInvoiceStatusReq] = useAxios<Res<null>>({
    method: 'GET',
  });

  const changeInvoiceStatus = useCallback(
    (invoiceIds: number[], isCorrective: boolean, value: boolean) => {
      const endpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return changeInvoiceStatusReq({
        url: `/${endpoint}/changeStatus`,
        params: {
          invoiceIds,
          value,
        },
        paramsSerializer: repeatArraySerializer,
      });
    },
    [changeInvoiceStatusReq],
  );

  return {
    loading,
    changeInvoiceStatus,
  };
};

export const useGetListSummary = () => {
  const [{ data, loading }, getListSummaryReq] = useAxios<{
    result: number;
  }>({
    method: 'GET',
  });

  const getListSummary = useCallback(
    (invoiceIds: number[]) =>
      getListSummaryReq({
        url: `/invoice/listSummary`,
        params: {
          invoiceIds,
        },
        paramsSerializer: repeatArraySerializer,
      }),
    [getListSummaryReq],
  );

  return {
    listSummaryData: data?.result,
    loading,
    getListSummary,
  };
};

export const useGetInvoiceByJobId = () => {
  const [{ data, loading }, getInvoiceByJobIdReq] = useAxios<{
    result: Invoice | CorrectiveInvoice;
  }>({
    method: 'GET',
  });

  const getInvoiceByJobId = useCallback(
    (jobId: number) => getInvoiceByJobIdReq({ url: `/invoice/job/${jobId}` }),
    [getInvoiceByJobIdReq],
  );

  return {
    data: data?.result,
    loading,
    getInvoiceByJobId,
  };
};

export const useCreateJobInvoice = () => {
  const [{ data, loading }, createJobInvoiceReq] = useAxios<Res<Invoice>>({
    method: 'POST',
  });

  const createJobInvoice = useCallback(
    (jobId: number, newInvoice: CreateJobInvoiceBody) =>
      createJobInvoiceReq({
        url: `/job/${jobId}/invoice`,
        data: newInvoice,
      }),
    [createJobInvoiceReq],
  );

  return {
    createdJobInvoiceData: data?.result,
    loading,
    createJobInvoice,
  };
};

export const usePublishInvoice = () => {
  const [{ data, loading }, publishInvoiceReq] = useAxios<
    Res<InvoicePublicationResult | null>
  >({
    method: 'PUT',
  });

  const publishInvoice = useCallback(
    (invoiceId: number, language: string, isCorrective: boolean) => {
      const baseEndpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return publishInvoiceReq({
        url: `/${baseEndpoint}/${invoiceId}/publish/${language}`,
      });
    },
    [publishInvoiceReq],
  );

  return {
    data: data?.result,
    loading,
    publishInvoice,
  };
};

export const useRevertInvoicePublication = () => {
  const [{ data, loading }, revertInvoicePublicationReq] = useAxios<Res<null>>({
    method: 'PUT',
  });

  const revertInvoicePublication = useCallback(
    (isCorrective: boolean, invoiceId: number) => {
      const baseEndpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return revertInvoicePublicationReq({
        url: `/${baseEndpoint}/${invoiceId}/revertPublish`,
      });
    },
    [revertInvoicePublicationReq],
  );

  return {
    data: data?.result,
    loading,
    revertInvoicePublication,
  };
};

export const useClearInvoiceCorrectionRequired = () => {
  const [{ data, loading }, clearInvoiceCorrectionRequiredReq] = useAxios<
    Res<null>
  >({
    method: 'PUT',
  });

  const clearInvoiceCorrectionRequired = useCallback(
    (invoiceId: number) =>
      clearInvoiceCorrectionRequiredReq({
        url: `/invoice/${invoiceId}/clearCorrectionRequired`,
      }),
    [clearInvoiceCorrectionRequiredReq],
  );

  return {
    data: data?.result,
    loading,
    clearInvoiceCorrectionRequired,
  };
};

export const usePrintInvoice = () => {
  const [isLoading, setIsLoading] = useState(false);

  const printInvoice = useCallback(
    (invoiceId: number, isCorrective: boolean, lang?: string) => {
      setIsLoading(true);
      const baseEndpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return axiosInstance
        .get<Blob | Res<unknown>>(`/${baseEndpoint}/${invoiceId}/pdf`, {
          params: { lang },
          responseType: 'blob',
        })
        .then((res) => {
          if (!(res.data instanceof Blob)) return;

          const fileName =
            res.headers['content-disposition']?.split('filename=')[1] ||
            `invoice-${invoiceId}.pdf`;
          fileDownload(res.data, fileName);
        })
        .catch((e) => {
          console.error(e);
          return undefined;
        })
        .finally(() => setIsLoading(false));
    },
    [],
  );

  return {
    isLoading,
    printInvoice,
  };
};

export const useDownloadInvoiceUpo = () => {
  const [{ loading }, downloadInvoiceUpoReq] = useAxios<Blob | Res<unknown>>({
    method: 'GET',
    responseType: 'blob',
  });

  const downloadInvoiceUpo = useCallback(
    async (invoiceId: number, ksefNumber: string, isCorrective: boolean) => {
      const baseEndpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      const { data } = await downloadInvoiceUpoReq({
        url: `/${baseEndpoint}/${invoiceId}/ksef/upo`,
      });

      if (data instanceof Blob) {
        fileDownload(data, `UPO_${ksefNumber}.xml`);
      }
    },
    [downloadInvoiceUpoReq],
  );

  return { downloadInvoiceUpo, loading };
};

export const useRemoveAllLines = () => {
  const [{ data, loading }, removeAllLinesReq] = useAxios<
    Res<Invoice | CorrectiveInvoice>
  >({
    method: 'PUT',
  });

  const removeAllLines = useCallback(
    (
      invoiceId: number,
      isCorrective: boolean,
      invoiceBody: EditInvoiceBody | EditCorrectiveInvoiceBody,
    ) => {
      const baseEndpoint = isCorrective ? 'correctiveinvoice' : 'invoice';
      return removeAllLinesReq({
        url: `/${baseEndpoint}/${invoiceId}/removeAllLines`,
        data: invoiceBody,
      });
    },
    [removeAllLinesReq],
  );
  return {
    data: data?.result,
    loading,
    removeAllLines,
  };
};

export const useGetMailStub = () => {
  const [{ data, loading }, getMailStubReq] = useAxios<Res<MailStub>>({
    method: 'GET',
  });

  const getMailStub = useCallback(
    (invoiceId: number, isCorrective: boolean) =>
      getMailStubReq({
        url: `/${
          isCorrective ? 'correctiveinvoice' : 'invoice'
        }/${invoiceId}/mail`,
      }),
    [getMailStubReq],
  );

  return {
    data: data?.result,
    loading,
    getMailStub,
  };
};

export const useSendMail = () => {
  // NOTE: Res body for successful req is unknown.
  const [{ data, loading }, sendMailReq] = useAxios<Res<null>>({
    method: 'POST',
  });

  const sendMail = useCallback(
    (data: MailReqBody, invoiceId: number, isCorrective: boolean) =>
      sendMailReq({
        url: `/${
          isCorrective ? 'correctiveinvoice' : 'invoice'
        }/${invoiceId}/mail`,
        data,
      }),
    [sendMailReq],
  );

  return {
    data: data?.result,
    loading,
    sendMail,
  };
};

export const getCheckUnpublished = async () =>
  await axiosInstance.get<Res<CheckUnpublishedResult>>(
    '/invoice/checkUnpublished',
  );

export const useFetchSaldeoDocuments = () => {
  const [{ loading }, fetchSaldeoDocumentsReq] = useAxios<Res<FetchResult>>(
    {
      method: 'POST',
    },
    {
      autoCancel: false,
    },
  );

  const fetchSaldeoDocuments = useCallback(
    () =>
      fetchSaldeoDocumentsReq({
        url: '/saldeo/documents/fetch',
        skipErrorToast: true,
      } as any),
    [fetchSaldeoDocumentsReq],
  );

  return {
    loading,
    fetchSaldeoDocuments,
  };
};

export const useFetchEnovaDocuments = () => {
  const [{ loading }, fetchEnovaDocumentsReq] = useAxios<Res<EnovaFetchResult>>(
    {
      method: 'POST',
    },
    {
      autoCancel: false,
    },
  );

  const fetchEnovaDocuments = useCallback(
    () =>
      fetchEnovaDocumentsReq({
        url: '/invoice/enova-confirmations/import',
        skipErrorToast: true,
      } as any),
    [fetchEnovaDocumentsReq],
  );

  return {
    loading,
    fetchEnovaDocuments,
  };
};
